import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface DeviceRoute {
  destinationSubnet: string;
  nextHopIp: string | null;
  interfaceName: string | null;
  interfaceIfindex: string | null;
  routeType: string | null;
  routeProtocol: string | null;
  metric: number | null;
  /** The VRF (routing table) the device named, when it names one. */
  vrf: string | null;
}

export interface DeviceRoutesResponse {
  observedAtMs: number;
  routes: DeviceRoute[];
}

interface RawRoute {
  destination_subnet: string;
  next_hop_ip: string | null;
  interface_name: string | null;
  interface_ifindex: string | null;
  route_type: string | null;
  route_protocol: string | null;
  metric: number | null;
  vrf?: string | null;
}

interface RawResponse {
  observed_at_ms: number;
  routes: RawRoute[];
}

// Backend ships snake_case (loco's default serde config); the rest of the
// frontend hooks expect camelCase. Convert at the boundary so consumers
// of this hook don't have to think about it.
function toCamel(raw: RawResponse): DeviceRoutesResponse {
  return {
    observedAtMs: raw.observed_at_ms,
    routes: raw.routes.map((r) => ({
      destinationSubnet: r.destination_subnet,
      nextHopIp: r.next_hop_ip,
      interfaceName: r.interface_name,
      interfaceIfindex: r.interface_ifindex,
      routeType: r.route_type,
      routeProtocol: r.route_protocol,
      metric: r.metric,
      vrf: r.vrf ?? null,
    })),
  };
}

/**
 * Fetch routing table state for a device.
 *
 * @param deviceId  device UUID
 * @param atMs      reconstruct state as of this absolute ms-since-epoch
 *                  timestamp. `null`/`undefined` → "now" (backend default).
 *                  Pass an explicit number to look back in time.
 */
export function useDeviceRoutes(deviceId: string | undefined, atMs: number | null | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceRoutesResponse>({
    // Including atMs in the query key means switching the time control
    // triggers a refetch even when the rest of the page state is stable.
    queryKey: ['deviceRoutes', deviceId, atMs ?? 'now'],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const params = atMs != null ? { at: atMs } : undefined;
      const response = await axios.get<RawResponse>(
        `${config.backendApi}/api/devices/${deviceId}/routes`,
        { withCredentials: true, params }
      );
      return toCamel(response.data);
    },
    enabled: !!deviceId && contextReady,
  });
}
